import { modals } from '@mantine/modals';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import { isPrivateEnvironment, isPublicEnvironment } from 'shared/utils/environment';
import { reducerState } from 'shared/utils/view';

import { ExitConfirm } from './ExitConfirm/ExitConfirm';

const INIT_STATE = {
  isActive: false,
  nextLocation: undefined,
  blockNavigation: undefined,
};

type Props = {
  when: boolean;
  disableNative?: boolean;
  redirect?: Function;
  onSaveAndQuit?: Function;
  canChangePage?: boolean;
};

type State = {
  nextLocation?: Location;
  isActive: boolean;
  unblock?: Function;
  blockNavigation?: boolean;
};

let unblock: Function;

function NavigationPromptInner({
  disableNative,
  canChangePage,
  when,
  redirect,
  onSaveAndQuit,
}: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const [state, setState] = useReducer<State, any>(reducerState, INIT_STATE);

  const onCancel = () => {
    setState({ ...INIT_STATE });
  };

  const onBeforeUnload = (e: any) => {
    // custom message doesn't work for security reason
    const msg = t(i18nKeys.AYS.EXIT_PAGE);
    e.returnValue = msg;
    return msg;
  };

  const block = (nextLocation: any, action: any): string | false | undefined => {
    if (isPrivateEnvironment()) return undefined;

    if (!canChangePage && when) {
      setState({
        nextLocation,
        isActive: true,
      });

      modals.open({
        title: t(i18nKeys.ATTENTION),
        centered: true,
        size: onSaveAndQuit ? 'md' : 'sm',
        children: (
          <ExitConfirm
            onCancel={() => {
              modals.closeAll();
              onCancel();
            }}
            onQuit={() => {
              modals.closeAll();
              navigateToNextLocation(nextLocation, action);
            }}
            onSaveAndQuit={
              onSaveAndQuit
                ? () => {
                    modals.closeAll();
                    onSaveAndQuit(() => navigateToNextLocation(nextLocation, action));
                  }
                : undefined
            }
          />
        ),
      });
    }

    return canChangePage || (!when as any);
  };

  useEffect(() => {
    if (when !== state.blockNavigation) {
      unblock = history.block(block);
      setState({
        blockNavigation: when,
      });

      if (!disableNative) {
        window.addEventListener('beforeunload', onBeforeUnload);
      }
    }

    return () => {
      unblock?.();
      if (!disableNative) {
        window.removeEventListener('beforeunload', onBeforeUnload);
      }
    };
  }, [when]);

  useEffect(() => {
    if (redirect) {
      unblock?.();
      redirect();
    }
  }, [redirect]);

  const navigateToNextLocation = (nextLocation: any, action: 'POP' | 'PUSH' | 'REPLACE') => {
    const currentAction: string = {
      POP: 'goBack',
      PUSH: 'push',
      REPLACE: 'replace',
    }[action || 'PUSH'];

    if (!nextLocation) nextLocation = { pathname: '/' };
    unblock!();
    if (currentAction === 'goBack') {
      // Because there is asynchronous time between calling history.goBack()
      // and history actually changing, we need to set up this temporary callback
      // -- if we tried to run this synchronously after calling history.goBack(),
      // then navigateToNextLocation would be triggered again.
      const unlisten = history.listen(() => {
        unlisten();
        setState({
          ...INIT_STATE,
        });
      });

      history.goBack();
      // @ts-expect-error
    } else history[currentAction](nextLocation);
  };

  return null;
}

export default function NavigationPrompt(props: Props) {
  return isPublicEnvironment() ? <NavigationPromptInner {...props} /> : null;
}
